import { Badge, Code, Text } from '@mantine/core';
import { auditApi } from '../../shared/api/audit';
import { AuditTable, type AuditColumn } from './AuditTable';
import {
  cargoEventTypeLabel,
  directionLabel,
  orderStatusLabel,
  paymentMethodLabel,
  quoteStatusLabel,
  shipmentStatusLabel,
  transportLabel,
} from '../../shared/ui/order-labels';
import { contractPartyRoleLabel } from '@expeditor/shared';

// ===========================================================================
// п.3 а) — Клиенты
// ===========================================================================
interface AuditClientRow {
  id: string;
  serviceId: string;
  type: string;
  displayName: string;
  phone: string | null;
  email: string | null;
  oneCExternalId: string | null;
  createdAt: string;
  deregisteredAt: string | null;
  isActive: boolean;
  details: Record<string, unknown> | null;
}

const CLIENT_TYPE_LABEL: Record<string, string> = {
  INDIVIDUAL: 'ФЛ',
  ENTREPRENEUR: 'ИП',
  LEGAL_RU: 'ЮЛ РФ',
  LEGAL_FOREIGN: 'Иностр.',
};

export function AuditClientsPage() {
  const cols: AuditColumn<AuditClientRow>[] = [
    { header: 'ID сервиса', render: (r) => <Code>{r.serviceId}</Code>, width: 150 },
    { header: 'Тип', render: (r) => <Badge>{CLIENT_TYPE_LABEL[r.type] ?? r.type}</Badge> },
    { header: 'Наименование', render: (r) => r.displayName },
    {
      header: 'Идентификаторы',
      render: (r) => {
        const d = r.details ?? {};
        const inn = d.inn as string | undefined;
        const kpp = d.kpp as string | undefined;
        const ogrn = (d.ogrn ?? d.ogrnip) as string | undefined;
        return (
          <Text size="xs">
            {inn && `ИНН ${inn}`}
            {kpp && ` · КПП ${kpp}`}
            {ogrn && ` · ${d.ogrnip ? 'ОГРНИП' : 'ОГРН'} ${ogrn}`}
          </Text>
        );
      },
    },
    { header: 'Контакт', render: (r) => r.phone ?? r.email ?? '—' },
    {
      header: 'Статус',
      render: (r) =>
        r.deregisteredAt ? (
          <Badge color="gray">прекращена</Badge>
        ) : r.isActive ? (
          <Badge color="green">активен</Badge>
        ) : (
          <Badge color="red">деактивирован</Badge>
        ),
    },
    {
      header: 'Создан',
      render: (r) => new Date(r.createdAt).toLocaleDateString('ru-RU'),
      width: 110,
    },
  ];
  return (
    <AuditTable
      title="Клиенты"
      clause="п.3 а) — Идентификация клиента"
      description={
        <Text size="sm">
          Полный перечень идентификационных данных. Для просмотра type-specific блока
          (паспорт ФЛ / реквизиты ЮЛ / иностранной орг.) — нажмите &lt;/&gt; в правой колонке.
        </Text>
      }
      fetcher={() => auditApi.clients()}
      columns={cols}
      expandable
      getExpandData={(r) => ({ ...r, details: r.details })}
    />
  );
}

// ===========================================================================
// п.3 б) — RegistrationEvent
// ===========================================================================
interface RegRow {
  id: string;
  clientName: string;
  occurredAt: string;
  timezone: string;
  clientIp: string;
  clientPort: number | null;
  serverIp: string;
  serverPort: number;
  userAgent: string | null;
  payload: Record<string, unknown>;
}

export function AuditRegistrationEventsPage() {
  const cols: AuditColumn<RegRow>[] = [
    {
      header: 'Время (с ЧП)',
      render: (r) => (
        <>
          <Text size="sm">{new Date(r.occurredAt).toLocaleString('ru-RU')}</Text>
          <Text size="xs" c="dimmed">
            {r.timezone}
          </Text>
        </>
      ),
      width: 200,
    },
    { header: 'Клиент', render: (r) => r.clientName },
    {
      header: 'IP клиента : порт',
      render: (r) => `${r.clientIp}${r.clientPort ? `:${r.clientPort}` : ''}`,
    },
    {
      header: 'IP сервиса : порт',
      render: (r) => `${r.serverIp}:${r.serverPort}`,
    },
    {
      header: 'User-Agent',
      render: (r) => (
        <Text size="xs" lineClamp={2}>
          {r.userAgent ?? '—'}
        </Text>
      ),
    },
  ];
  return (
    <AuditTable
      title="События регистрации"
      clause="п.3 б)"
      description={
        <Text size="sm">
          На каждую регистрацию записывается ровно один RegistrationEvent: точное время с
          часовым поясом, сетевые адреса/порты клиента и сервиса, User-Agent, и полный payload
          (см. кнопку &lt;/&gt;). Пароли в payload отсутствуют.
        </Text>
      }
      fetcher={(f) => auditApi.registrationEvents(f)}
      columns={cols}
      expandable
      getExpandData={(r) => r.payload}
    />
  );
}

// ===========================================================================
// п.3 в) — AuthEvent
// ===========================================================================
interface AuthRow {
  id: string;
  userEmail: string;
  occurredAt: string;
  timezone: string;
  success: boolean;
  clientIp: string;
  clientPort: number | null;
  serverIp: string;
  serverPort: number;
  userAgent: string | null;
}

export function AuditAuthEventsPage() {
  const cols: AuditColumn<AuthRow>[] = [
    {
      header: 'Время (с ЧП)',
      render: (r) => (
        <>
          <Text size="sm">{new Date(r.occurredAt).toLocaleString('ru-RU')}</Text>
          <Text size="xs" c="dimmed">
            {r.timezone}
          </Text>
        </>
      ),
      width: 200,
    },
    { header: 'Пользователь', render: (r) => r.userEmail },
    {
      header: 'Результат',
      render: (r) =>
        r.success ? <Badge color="green">УСПЕХ</Badge> : <Badge color="red">ОТКАЗ</Badge>,
    },
    { header: 'IP клиента', render: (r) => `${r.clientIp}${r.clientPort ? `:${r.clientPort}` : ''}` },
    { header: 'Сервер', render: (r) => `${r.serverIp}:${r.serverPort}` },
    {
      header: 'User-Agent',
      render: (r) => (
        <Text size="xs" lineClamp={2}>
          {r.userAgent ?? '—'}
        </Text>
      ),
    },
  ];
  return (
    <AuditTable
      title="Входы в систему"
      clause="п.3 в)"
      description={
        <Text size="sm">
          Каждая попытка входа — отдельная запись. Успешные и неуспешные журналируются
          одинаково (поле «Результат»). Это позволяет регулятору видеть в том числе
          попытки подбора пароля.
        </Text>
      }
      fetcher={(f) => auditApi.authEvents(f)}
      columns={cols}
    />
  );
}

// ===========================================================================
// п.3 г) — ProfileChangeEvent
// ===========================================================================
interface PCRow {
  id: string;
  clientName: string;
  occurredAt: string;
  changedByEmail: string | null;
  fieldPath: string;
  oldValue: unknown;
  newValue: unknown;
}

export function AuditProfileChangesPage() {
  const cols: AuditColumn<PCRow>[] = [
    {
      header: 'Время',
      render: (r) => new Date(r.occurredAt).toLocaleString('ru-RU'),
      width: 170,
    },
    { header: 'Клиент', render: (r) => r.clientName },
    { header: 'Изменил', render: (r) => r.changedByEmail ?? '—' },
    {
      header: 'Поле',
      render: (r) => <Code>{r.fieldPath}</Code>,
    },
    {
      header: 'Было',
      render: (r) => <Code>{JSON.stringify(r.oldValue) ?? 'null'}</Code>,
    },
    {
      header: 'Стало',
      render: (r) => <Code>{JSON.stringify(r.newValue) ?? 'null'}</Code>,
    },
  ];
  return (
    <AuditTable
      title="Изменения профиля"
      clause="п.3 г)"
      description={
        <Text size="sm">
          На каждое изменённое поле — отдельная запись (старое и новое значение). Один PATCH,
          затронувший 3 поля, создаст 3 записи.
        </Text>
      }
      fetcher={(f) => auditApi.profileChanges(f)}
      columns={cols}
    />
  );
}

// ===========================================================================
// п.3 д) — DeregistrationEvent
// ===========================================================================
interface DeregRow {
  id: string;
  clientName: string;
  occurredAt: string;
  timezone: string;
  clientIp: string;
  serverIp: string;
  serverPort: number;
  reason: string | null;
}

export function AuditDeregistrationsPage() {
  const cols: AuditColumn<DeregRow>[] = [
    {
      header: 'Время (ЧП)',
      render: (r) => (
        <>
          <Text size="sm">{new Date(r.occurredAt).toLocaleString('ru-RU')}</Text>
          <Text size="xs" c="dimmed">
            {r.timezone}
          </Text>
        </>
      ),
    },
    { header: 'Клиент', render: (r) => r.clientName },
    { header: 'IP клиента', render: (r) => r.clientIp },
    { header: 'Сервер', render: (r) => `${r.serverIp}:${r.serverPort}` },
    { header: 'Причина', render: (r) => r.reason ?? '—' },
  ];
  return (
    <AuditTable
      title="Прекращение регистрации"
      clause="п.3 д)"
      description={
        <Text size="sm">
          Прекращение регистрации = мягкая деактивация: User.isActive=false, Client.deregisteredAt=now.
          Записи клиента физически НЕ удаляются (требование ретеншна 3 года).
        </Text>
      }
      fetcher={(f) => auditApi.deregistrations(f)}
      columns={cols}
    />
  );
}

// ===========================================================================
// п.3 е) — Orders
// ===========================================================================
interface OrderRow {
  id: string;
  number: string;
  status: string;
  transportMode: string;
  direction: string;
  cargoName: string;
  cargoIsDangerous: boolean;
  cargoAdrClass: string | null;
  cargoWeightKg: number;
  cargoValue: number | null;
  clientName: string;
  createdAt: string;
}

export function AuditOrdersPage() {
  const cols: AuditColumn<OrderRow>[] = [
    { header: 'Номер', render: (r) => <Code>{r.number}</Code>, width: 130 },
    { header: 'Клиент', render: (r) => r.clientName },
    { header: 'Статус', render: (r) => <Badge>{orderStatusLabel(r.status)}</Badge> },
    { header: 'Транспорт', render: (r) => `${transportLabel(r.transportMode)} / ${directionLabel(r.direction)}` },
    { header: 'Груз', render: (r) => r.cargoName },
    {
      header: 'Опасный',
      render: (r) =>
        r.cargoIsDangerous ? <Badge color="red">ДОПОГ {r.cargoAdrClass}</Badge> : '—',
    },
    { header: 'Вес, кг', render: (r) => r.cargoWeightKg },
    { header: 'Стоимость', render: (r) => (r.cargoValue !== null ? r.cargoValue.toLocaleString('ru-RU') : '—') },
    {
      header: 'Создано',
      render: (r) => new Date(r.createdAt).toLocaleDateString('ru-RU'),
      width: 110,
    },
  ];
  return (
    <AuditTable
      title="Поручения"
      clause="п.3 е)"
      fetcher={(f) => auditApi.orders(f)}
      columns={cols}
    />
  );
}

// ===========================================================================
// п.3 е) — Quotes
// ===========================================================================
interface QuoteRow {
  id: string;
  orderNumber: string;
  clientName: string;
  amount: number;
  currency: string;
  status: string;
  workScope: string;
  validUntil: string;
  createdAt: string;
}

export function AuditQuotesPage() {
  const cols: AuditColumn<QuoteRow>[] = [
    {
      header: 'Поручение',
      render: (r) => <Code>{r.orderNumber}</Code>,
      width: 130,
    },
    { header: 'Клиент', render: (r) => r.clientName },
    {
      header: 'Сумма',
      render: (r) => (
        <Text fw={500}>
          {r.amount.toLocaleString('ru-RU')} {r.currency}
        </Text>
      ),
    },
    { header: 'Статус', render: (r) => <Badge>{quoteStatusLabel(r.status)}</Badge> },
    {
      header: 'Состав работ',
      render: (r) => (
        <Text size="sm" lineClamp={2}>
          {r.workScope}
        </Text>
      ),
    },
    {
      header: 'Действительна до',
      render: (r) => new Date(r.validUntil).toLocaleDateString('ru-RU'),
    },
  ];
  return (
    <AuditTable
      title="Котировки"
      clause="п.3 е)"
      fetcher={(f) => auditApi.quotes(f)}
      columns={cols}
    />
  );
}

// ===========================================================================
// п.3 е) — Contracts
// ===========================================================================
interface ContractRow {
  id: string;
  number: string;
  orderNumber: string;
  clientName: string;
  subject: string;
  signedAt: string;
  endedAt: string | null;
  partiesCount: number;
}

export function AuditContractsPage() {
  const cols: AuditColumn<ContractRow>[] = [
    { header: 'Договор', render: (r) => <Code>{r.number}</Code>, width: 130 },
    { header: 'Поручение', render: (r) => <Code>{r.orderNumber}</Code>, width: 130 },
    { header: 'Клиент', render: (r) => r.clientName },
    {
      header: 'Предмет',
      render: (r) => (
        <Text size="sm" lineClamp={2}>
          {r.subject}
        </Text>
      ),
    },
    {
      header: 'Подписан',
      render: (r) => new Date(r.signedAt).toLocaleDateString('ru-RU'),
      width: 100,
    },
    {
      header: 'Завершён',
      render: (r) =>
        r.endedAt ? (
          new Date(r.endedAt).toLocaleDateString('ru-RU')
        ) : (
          <Badge color="green">действует</Badge>
        ),
      width: 110,
    },
    { header: 'Привлечено исполнителей', render: (r) => r.partiesCount, width: 100 },
  ];
  return (
    <AuditTable
      title="Договоры (контракты)"
      clause="п.3 е)"
      description={
        <Text size="sm">
          Договор возникает автоматически при акцепте котировки клиентом. При закрытии
          перевозки проставляется <code>endedAt</code> — стартует ретеншн 3 года.
        </Text>
      }
      fetcher={(f) => auditApi.contracts(f)}
      columns={cols}
    />
  );
}

// ===========================================================================
// п.3 ж) — ContractParty
// ===========================================================================
interface PartyRow {
  id: string;
  contractNumber: string;
  clientName: string;
  role: string;
  notes: string | null;
  carrier: { fullName: string; inn: string | null; type: string } | null;
}

export function AuditContractPartiesPage() {
  const cols: AuditColumn<PartyRow>[] = [
    { header: 'Договор', render: (r) => <Code>{r.contractNumber}</Code> },
    { header: 'Клиент', render: (r) => r.clientName },
    { header: 'Роль', render: (r) => <Badge>{contractPartyRoleLabel(r.role)}</Badge> },
    {
      header: 'Перевозчик',
      render: (r) =>
        r.carrier ? (
          <>
            <Text size="sm">{r.carrier.fullName}</Text>
            {r.carrier.inn && (
              <Text size="xs" c="dimmed">
                ИНН {r.carrier.inn} · {r.carrier.type}
              </Text>
            )}
          </>
        ) : (
          <Text c="dimmed">без привязки к справочнику</Text>
        ),
    },
    {
      header: 'Заметки',
      render: (r) => (
        <Text size="sm" lineClamp={2}>
          {r.notes ?? '—'}
        </Text>
      ),
    },
  ];
  return (
    <AuditTable
      title="Привлекаемые исполнители"
      clause="п.3 ж)"
      fetcher={() => auditApi.contractParties()}
      columns={cols}
    />
  );
}

// ===========================================================================
// п.3 з) — Shipments
// ===========================================================================
interface ShipRow {
  id: string;
  contractNumber: string;
  orderNumber: string;
  clientName: string;
  cargoName: string;
  status: string;
  startedAt: string | null;
  finishedAt: string | null;
}

export function AuditShipmentsPage() {
  const cols: AuditColumn<ShipRow>[] = [
    { header: 'Договор', render: (r) => <Code>{r.contractNumber}</Code> },
    { header: 'Поручение', render: (r) => <Code>{r.orderNumber}</Code> },
    { header: 'Клиент', render: (r) => r.clientName },
    { header: 'Груз', render: (r) => r.cargoName },
    { header: 'Статус', render: (r) => <Badge>{shipmentStatusLabel(r.status)}</Badge> },
    {
      header: 'В пути с',
      render: (r) => (r.startedAt ? new Date(r.startedAt).toLocaleString('ru-RU') : '—'),
    },
    {
      header: 'Доставлен',
      render: (r) => (r.finishedAt ? new Date(r.finishedAt).toLocaleString('ru-RU') : '—'),
    },
  ];
  return (
    <AuditTable title="Перевозки" clause="п.3 з)" fetcher={() => auditApi.shipments()} columns={cols} />
  );
}

// ===========================================================================
// п.3 з) — CargoEvent
// ===========================================================================
interface CERow {
  id: string;
  contractNumber: string;
  clientName: string;
  type: string;
  occurredAt: string;
  locationName: string | null;
  latitude: number | null;
  longitude: number | null;
  comment: string | null;
}

export function AuditCargoEventsPage() {
  const cols: AuditColumn<CERow>[] = [
    {
      header: 'Время',
      render: (r) => new Date(r.occurredAt).toLocaleString('ru-RU'),
      width: 170,
    },
    { header: 'Договор', render: (r) => <Code>{r.contractNumber}</Code> },
    { header: 'Клиент', render: (r) => r.clientName },
    { header: 'Тип', render: (r) => <Badge>{cargoEventTypeLabel(r.type)}</Badge> },
    { header: 'Место', render: (r) => r.locationName ?? '—' },
    {
      header: 'Координаты',
      render: (r) =>
        r.latitude !== null && r.longitude !== null ? `${r.latitude.toFixed(4)}, ${r.longitude.toFixed(4)}` : '—',
    },
    {
      header: 'Комментарий',
      render: (r) => (
        <Text size="sm" lineClamp={2}>
          {r.comment ?? '—'}
        </Text>
      ),
    },
  ];
  return (
    <AuditTable
      title="События груза"
      clause="п.3 з)"
      description={
        <Text size="sm">
          Append-only журнал. Тип события автоматически меняет статус перевозки
          (RECEIVED → PICKED_UP → LOADED → IN_TRANSIT → DELIVERED).
        </Text>
      }
      fetcher={(f) => auditApi.cargoEvents(f)}
      columns={cols}
    />
  );
}

// ===========================================================================
// п.3 к) — GeoTrack
// ===========================================================================
interface GTRow {
  id: string;
  shipmentId: string;
  contractNumber: string;
  occurredAt: string;
  latitude: number;
  longitude: number;
  speed: number | null;
  source: string | null;
}

export function AuditGeoTrackPage() {
  const cols: AuditColumn<GTRow>[] = [
    {
      header: 'Время',
      render: (r) => new Date(r.occurredAt).toLocaleString('ru-RU'),
      width: 170,
    },
    { header: 'Договор', render: (r) => <Code>{r.contractNumber}</Code> },
    {
      header: 'Координаты',
      render: (r) => (
        <Code>
          {r.latitude.toFixed(5)}, {r.longitude.toFixed(5)}
        </Code>
      ),
    },
    { header: 'Скорость, км/ч', render: (r) => (r.speed !== null ? r.speed : '—') },
    { header: 'Источник', render: (r) => r.source ?? '—' },
  ];
  return (
    <AuditTable
      title="Точки геолокации"
      clause="п.3 к)"
      description={
        <Text size="sm">
          Источник MANUAL — ввод менеджером, BATCH_IMPORT — пачкой из файла,
          TELEMATICS — push от внешней телематической системы.
        </Text>
      }
      fetcher={(f) => auditApi.geoTrack(f)}
      columns={cols}
    />
  );
}

// ===========================================================================
// п.3 и) — Payments
// ===========================================================================
interface PRow {
  id: string;
  contractNumber: string | null;
  clientName: string | null;
  amount: number;
  currency: string;
  method: string;
  purpose: string;
  counterpartyName: string | null;
  paymentSystemId: string | null;
  occurredAt: string;
  source: string;
  oneCExternalId: string | null;
}

export function AuditPaymentsPage() {
  const cols: AuditColumn<PRow>[] = [
    {
      header: 'Время',
      render: (r) => new Date(r.occurredAt).toLocaleString('ru-RU'),
      width: 170,
    },
    { header: 'Договор', render: (r) => (r.contractNumber ? <Code>{r.contractNumber}</Code> : '—') },
    { header: 'Клиент', render: (r) => r.clientName ?? '—' },
    {
      header: 'Сумма',
      render: (r) => (
        <Text fw={500}>
          {r.amount.toLocaleString('ru-RU')} {r.currency}
        </Text>
      ),
    },
    { header: 'Способ', render: (r) => r.method },
    {
      header: 'Назначение',
      render: (r) => (
        <Text size="sm" lineClamp={2}>
          {r.purpose}
        </Text>
      ),
    },
    {
      header: 'Корреспондент',
      render: (r) =>
        r.counterpartyName ?? (r.paymentSystemId ? <Code>{r.paymentSystemId}</Code> : '—'),
    },
    {
      header: 'Источник',
      render: (r) => (
        <Badge color={r.source === 'ONEC_PUSH' ? 'blue' : 'gray'}>
          {r.source === 'ONEC_PUSH' ? 'Из 1С' : 'Вручную'}
        </Badge>
      ),
    },
    {
      header: 'ID в 1С',
      render: (r) => (r.oneCExternalId ? <Code>{r.oneCExternalId}</Code> : '—'),
    },
  ];
  return (
    <AuditTable
      title="Платежи"
      clause="п.3 и)"
      fetcher={(f) => auditApi.payments(f)}
      columns={cols}
    />
  );
}

// ===========================================================================
// AuditLog
// ===========================================================================
interface ALRow {
  id: string;
  occurredAt: string;
  userEmail: string | null;
  action: string;
  entityType: string;
  entityId: string | null;
  clientIp: string | null;
  userAgent: string | null;
  payload: unknown;
}

export function AuditLogsPage() {
  const cols: AuditColumn<ALRow>[] = [
    {
      header: 'Время',
      render: (r) => new Date(r.occurredAt).toLocaleString('ru-RU'),
      width: 170,
    },
    { header: 'Кто', render: (r) => r.userEmail ?? <Text c="dimmed">неавторизован</Text> },
    { header: 'Действие', render: (r) => <Code>{r.action}</Code> },
    { header: 'Сущность', render: (r) => <Badge variant="light">{r.entityType}</Badge> },
    { header: 'IP', render: (r) => r.clientIp ?? '—' },
    {
      header: 'User-Agent',
      render: (r) => (
        <Text size="xs" lineClamp={1}>
          {r.userAgent ?? '—'}
        </Text>
      ),
    },
  ];
  return (
    <AuditTable
      title="AuditLog (все действия)"
      clause="кросс-cutting слой"
      description={
        <Text size="sm">
          Глобальный журнал всех mutating-эндпоинтов (POST/PUT/PATCH/DELETE).
          Чувствительные поля (пароли, refresh-токены) редактируются перед записью.
        </Text>
      }
      fetcher={(f) => auditApi.auditLogs(f)}
      columns={cols}
      expandable
      getExpandData={(r) => r.payload}
    />
  );
}
